import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Select, Button, useToast
} from '@chakra-ui/react';
import { authAPI } from '../services/api';

const ClassFormModal = ({ isOpen, onClose, onSuccess, teachers = [], institutions = [], departments = [], user, editData = null }) => {
    const toast = useToast();
    const isEditMode = !!editData;
    const [formData, setFormData] = useState({ name: '', teacher_id: '', institution_id: '', department: 'AfterSchool' });
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setFormData(editData
            ? { name: editData.name, teacher_id: editData.teacher_id || '', institution_id: editData.institution_id || '', department: editData.department || 'AfterSchool' }
            : { name: '', teacher_id: '', institution_id: '', department: 'AfterSchool' }
        );
    }, [isOpen, editData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'institution_id' ? { teacher_id: '' } : {}) }));
    };

    const filteredTeachers = user?.role === 'super_admin'
        ? (formData.institution_id ? teachers.filter(t => t.institution_id === parseInt(formData.institution_id)) : [])
        : teachers;

    const handleSubmit = async () => {
        if (!formData.name) { toast({ title: '請輸入班級名稱', status: 'warning' }); return; }
        if (user?.role === 'super_admin' && !formData.institution_id) { toast({ title: '請先選擇所屬機構', status: 'warning' }); return; }
        setSubmitLoading(true);
        try {
            const payload = {
                name: formData.name,
                teacher_id: formData.teacher_id ? parseInt(formData.teacher_id) : 0,
                department: formData.department || 'AfterSchool',
                ...(formData.institution_id ? { institution_id: parseInt(formData.institution_id) } : {})
            };
            if (isEditMode) {
                await authAPI.updateClass(editData.id, payload);
                toast({ title: '儲存成功', status: 'success' });
            } else {
                if (payload.teacher_id === 0) delete payload.teacher_id;
                await authAPI.createClass(payload);
                toast({ title: '班級新增成功', status: 'success' });
            }
            onSuccess();
            onClose();
        } catch (err) {
            toast({ title: isEditMode ? '更新失敗' : '新增失敗', description: err.response?.data?.detail, status: 'error' });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEditMode ? '編輯班級' : '新增班級'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {user?.role === 'super_admin' && (
                        <FormControl isRequired mb={4} isDisabled={isEditMode}>
                            <FormLabel>所屬機構</FormLabel>
                            <Select name="institution_id" placeholder="請先選擇機構" value={formData.institution_id} onChange={handleInputChange}>
                                {institutions.map(inst => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl isRequired mb={4}>
                        <FormLabel>班級名稱</FormLabel>
                        <Input name="name" placeholder="例如: 小一數學班" value={formData.name} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl mb={4}>
                        <FormLabel>所屬部門</FormLabel>
                        <Select name="department" value={formData.department} onChange={handleInputChange}>
                            {departments.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </Select>
                    </FormControl>
                    <FormControl mb={4}>
                        <FormLabel>帶班老師</FormLabel>
                        <Select
                            name="teacher_id"
                            placeholder={user?.role === 'super_admin' && !formData.institution_id ? '請先選擇機構' : '未指派 (或選擇老師)'}
                            value={formData.teacher_id}
                            onChange={handleInputChange}
                            isDisabled={user?.role === 'super_admin' && !formData.institution_id}
                        >
                            {filteredTeachers.map(t => <option key={t.id} value={t.id}>{t.full_name} ({t.phone_number})</option>)}
                        </Select>
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3}>取消</Button>
                    <Button colorScheme="brand" onClick={handleSubmit} isLoading={submitLoading}>
                        {isEditMode ? '儲存變更' : '確認新增'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ClassFormModal;
